import { useEffect, useState, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  AppLayout,
  type AppLayoutApi,
  Navbar,
  NavbarLink,
  NavbarSearch,
  Sidebar,
  SidebarHeader,
  SidebarNav,
  SidebarNavHeader,
  SidebarNavItem,
  Avatar,
  Footer,
} from '@jordan/ui';
import { useAuth } from '../auth/AuthProvider';
import { useMe } from '../auth/MeProvider';
import { useConfig } from '../config/ConfigProvider';
import { assetUrl, logoUrl } from '../lib/api';

// Reference (Limitless) and Storybook are served at /reference and /storybook in
// production (via Caddy), but on their own dev servers locally. Prefer an explicit
// VITE_* override; otherwise pick the right target based on the current host so the
// links work in both dev and production without relying on env wiring.
const isLocalHost =
  typeof window !== 'undefined' &&
  /^(localhost|127\.0\.0\.1|\[::1\])$/.test(window.location.hostname);
const referenceUrl =
  import.meta.env.VITE_REFERENCE_URL ?? (isLocalHost ? 'http://localhost:8080/' : '/reference/');
const storybookUrl =
  import.meta.env.VITE_STORYBOOK_URL ?? (isLocalHost ? 'http://localhost:6006/' : '/storybook/');
// Portainer runs on its own subdomain in production (portainer.<apex domain>) and on :9000
// locally — so it can't be a same-origin subpath like the two above. Derive the subdomain
// from the current host; override with VITE_PORTAINER_URL if needed.
const portainerUrl =
  import.meta.env.VITE_PORTAINER_URL ??
  (isLocalHost
    ? 'http://localhost:9000/'
    : `${window.location.protocol}//portainer.${window.location.hostname.split('.').slice(1).join('.')}/`);
// Grafana is only self-hosted in production; local dev points at the same production instance
// (there's no local Grafana). Override with VITE_GRAFANA_URL if needed.
const grafanaUrl = import.meta.env.VITE_GRAFANA_URL ?? 'https://grafana.digitalbaxter.com/';

/** Shared app shell (sticky navbar + sidebar) used by all authenticated pages. */
export function AppChrome({
  children,
  secondarySidebar,
}: {
  children: ReactNode;
  /**
   * Optional page-level second sidebar (light) rendered beside the main one. Receives the
   * layout API; pass `mobileExpanded={api.secondarySidebarExpanded}` and
   * `onClose={api.closeSecondarySidebar}` to <SecondarySidebar/>.
   */
  secondarySidebar?: (api: AppLayoutApi) => ReactNode;
}) {
  const { user, signOut } = useAuth();
  const me = useMe();
  const cfg = useConfig();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const isAdmin = me?.role === 'ADMIN';

  const displayName = me?.displayName ?? user?.displayName ?? user?.email ?? 'Account';
  const initials = (me?.displayName ?? user?.displayName ?? user?.email ?? '?').slice(0, 2);
  // Prefer the profile avatar (DB) over the Firebase photo.
  const avatarSrc = assetUrl(me?.photoUrl) ?? user?.photoURL ?? undefined;

  const [userMenuOpen, setUserMenuOpen] = useState(false);
  useEffect(() => {
    if (!userMenuOpen) return;
    const onDoc = (e: MouseEvent) => {
      if (!(e.target as HTMLElement).closest('.user-menu')) setUserMenuOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setUserMenuOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [userMenuOpen]);

  const logout = async () => {
    setUserMenuOpen(false);
    await signOut();
    navigate('/login');
  };
  const goTo = (path: string) => {
    setUserMenuOpen(false);
    navigate(path);
  };

  const brand = (
    <a
      href="/"
      className="d-inline-flex align-items-center"
      onClick={(e) => {
        e.preventDefault();
        navigate('/');
      }}
    >
      {cfg.hasLogo ? (
        <img src={logoUrl(cfg.logoUpdatedAt)} alt={cfg.appName} style={{ height: 22 }} />
      ) : (
        <span className="fw-bold fs-5 text-white">{cfg.appName}</span>
      )}
    </a>
  );

  const copyright = cfg.copyright.replace('{year}', String(new Date().getFullYear()));

  return (
    <AppLayout
      footer={<Footer copyright={copyright} />}
      secondarySidebar={secondarySidebar}
      navbar={({ toggleSidebar, toggleSecondarySidebar }) => (
        <Navbar
          onToggleSidebar={toggleSidebar}
          onToggleSecondarySidebar={secondarySidebar ? toggleSecondarySidebar : undefined}
          brand={brand}
          search={<NavbarSearch />}
          end={
            <>
              <NavbarLink icon="ph-bell" badge={2} />
              <NavbarLink
                dropdown
                controlled
                expanded={userMenuOpen}
                className="user-menu"
                onClick={() => setUserMenuOpen((o) => !o)}
                leading={<Avatar size={32} status="success" src={avatarSrc} initials={initials} />}
                label={displayName}
              >
                <div className={`dropdown-menu dropdown-menu-end ${userMenuOpen ? 'show' : ''}`.trim()}>
                  <span className="dropdown-item-text text-muted small">{me?.email ?? user?.email}</span>
                  <div className="dropdown-divider" />
                  <button className="dropdown-item" onClick={() => goTo('/profile')}>
                    <i className="ph-user-circle me-2" /> My profile
                  </button>
                  {isAdmin && (
                    <button className="dropdown-item" onClick={() => goTo('/configuration/general')}>
                      <i className="ph-gear me-2" /> Configuration
                    </button>
                  )}
                  <div className="dropdown-divider" />
                  <button className="dropdown-item" onClick={() => void logout()}>
                    <i className="ph-sign-out me-2" /> Logout
                  </button>
                </div>
              </NavbarLink>
            </>
          }
        />
      )}
      sidebar={({ sidebarExpanded, closeSidebar, sidebarResized, toggleSidebarResize }) => (
        <Sidebar mobileExpanded={sidebarExpanded} resized={sidebarResized}>
          <SidebarHeader onClose={closeSidebar} onToggleResize={toggleSidebarResize} />
          <SidebarNav>
            <SidebarNavHeader label="Main" first />
            <SidebarNavItem
              icon="ph-house"
              label="Dashboard"
              active={pathname === '/'}
              onClick={() => {
                navigate('/');
                closeSidebar();
              }}
            />
            <SidebarNavItem
              icon="ph-calendar"
              label="Scheduling"
              active={pathname.startsWith('/openslot')}
              onClick={() => {
                navigate('/openslot');
                closeSidebar();
              }}
            />
            <SidebarNavItem
              icon="ph-currency-circle-dollar"
              label="Billing & Time"
              active={pathname.startsWith('/consulting')}
              onClick={() => {
                navigate('/consulting/billing-time');
                closeSidebar();
              }}
            />
            <SidebarNavItem
              icon="ph-brain"
              label="AI"
              active={pathname.startsWith('/ai')}
              onClick={() => {
                navigate('/ai');
                closeSidebar();
              }}
            />

            <SidebarNavHeader label="Personal" />
            <SidebarNavItem
              icon="ph-heart"
              label="Health"
              active={pathname.startsWith('/health/oura')}
              defaultOpen={pathname.startsWith('/health/oura')}
            >
              <SidebarNavItem
                label="Oura Ring"
                active={pathname === '/health/oura'}
                onClick={() => {
                  navigate('/health/oura');
                  closeSidebar();
                }}
              />
            </SidebarNavItem>
            <SidebarNavItem
              icon="ph-prohibit-inset"
              label="Golf"
              active={pathname.startsWith('/health/golf')}
              onClick={() => {
                navigate('/health/golf/scores');
                closeSidebar();
              }}
            />
            <SidebarNavItem
              icon="ph-currency-circle-dollar"
              label="Maxsold"
              active={pathname.startsWith('/maxsold')}
              onClick={() => {
                navigate('/maxsold/favourites');
                closeSidebar();
              }}
            />

            <SidebarNavHeader label="Resources" />
            {isAdmin && (
              <SidebarNavItem
                icon="ph-google-photos-logo"
                label="Media"
                active={pathname.startsWith('/media')}
                onClick={() => {
                  navigate('/media/images');
                  closeSidebar();
                }}
              />
            )}
            {isAdmin && (
              <SidebarNavItem
                icon="ph-cloud"
                label="Cloud Management"
                active={pathname.startsWith('/cloud')}
                onClick={() => {
                  navigate('/cloud/dns');
                  closeSidebar();
                }}
              />
            )}
            {isAdmin && (
              <SidebarNavItem
                icon="ph-receipt"
                label="Billing"
                active={pathname.startsWith('/billing')}
                onClick={() => {
                  navigate('/billing');
                  closeSidebar();
                }}
              />
            )}
            {isAdmin && (
              <SidebarNavItem
                icon="ph-gear"
                label="Configuration"
                active={pathname.startsWith('/configuration')}
                defaultOpen={pathname.startsWith('/configuration')}
              >
                <SidebarNavItem
                  label="General"
                  active={pathname === '/configuration/general'}
                  onClick={() => {
                    navigate('/configuration/general');
                    closeSidebar();
                  }}
                />
                <SidebarNavItem
                  label="Connections"
                  active={pathname === '/configuration/connections'}
                  onClick={() => {
                    navigate('/configuration/connections');
                    closeSidebar();
                  }}
                />
                <SidebarNavItem
                  label="Scheduling"
                  active={pathname.startsWith('/configuration/openslot')}
                  onClick={() => {
                    navigate('/configuration/openslot');
                    closeSidebar();
                  }}
                />
                <SidebarNavItem
                  label="Billing"
                  active={pathname.startsWith('/configuration/billing')}
                  onClick={() => {
                    navigate('/configuration/billing');
                    closeSidebar();
                  }}
                />
                <SidebarNavItem
                  label="Billing & Time"
                  active={pathname.startsWith('/configuration/consulting')}
                  onClick={() => {
                    navigate('/configuration/consulting');
                    closeSidebar();
                  }}
                />
                <SidebarNavItem
                  label="Health"
                  active={pathname.startsWith('/configuration/health')}
                  onClick={() => {
                    navigate('/configuration/health');
                    closeSidebar();
                  }}
                />
                <SidebarNavItem
                  label="AI"
                  active={pathname.startsWith('/configuration/ai')}
                  onClick={() => {
                    navigate('/configuration/ai');
                    closeSidebar();
                  }}
                />
                <SidebarNavItem
                  label="Maxsold"
                  active={pathname.startsWith('/configuration/maxsold')}
                  onClick={() => {
                    navigate('/configuration/maxsold');
                    closeSidebar();
                  }}
                />
              </SidebarNavItem>
            )}

            <SidebarNavHeader label="Developer" />
            <SidebarNavItem
              icon="ph-book-open"
              label="Component reference"
              href={referenceUrl}
              target="_blank"
            />
            <SidebarNavItem
              icon="ph-stack"
              label="Storybook"
              href={storybookUrl}
              target="_blank"
            />
            <SidebarNavItem
              icon="ph-cube"
              label="Portainer"
              href={portainerUrl}
              target="_blank"
            />
            <SidebarNavItem
              icon="ph-chart-line"
              label="Grafana"
              href={grafanaUrl}
              target="_blank"
            />
          </SidebarNav>
        </Sidebar>
      )}
    >
      {children}
    </AppLayout>
  );
}
